import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
} from '@tanstack/react-query';
import { serviceApiV3 } from '@/api/client';
import {
  type Envelope,
  type VendorReadState,
  manticoreKeys,
  unwrapFanoutArray,
  parseCoreRead,
  firstVendor,
} from './_shared';
import type { CacheKeyConfig, CacheVaryConfig } from './domains';

// ============================================
// Upstream / Origin  (GET/PUT/DELETE /cdn/domains/:d/upstream)
// ============================================
// Config is a loose record (many optional fields, vendor variants) — the
// tab's form layer (upstream/form.ts) owns hydration + payload shaping.
export type UpstreamConfig = Record<string, unknown>;

export interface UpstreamReadResult {
  /** The CORE (vEdge) binding's config — the single source of truth. */
  config: UpstreamConfig | null;
  /** Core provider name; empty = the domain has no core binding (anomaly). */
  coreProvider: string;
  /** Per-vendor read outcome — failures are explicit, never silently dropped. */
  vendors: Record<string, VendorReadState>;
}

export function useUpstreamConfig(domain: string | undefined) {
  return useQuery({
    queryKey: manticoreKeys.upstream(domain ?? ''),
    enabled: !!domain,
    retry: false,
    queryFn: async (): Promise<UpstreamReadResult> => {
      try {
        const res = await serviceApiV3.get<Envelope<unknown>>(`/cdn/domains/${domain}/upstream`);
        const { core, coreProvider, vendors } = parseCoreRead<UpstreamConfig>(res.data?.data);
        return { config: core, coreProvider, vendors };
      } catch (e) {
        const status = (e as { response?: { status?: number } })?.response?.status;
        if (status === 400 || status === 404) return { config: null, coreProvider: '', vendors: {} };
        throw e;
      }
    },
  });
}

export function useUpdateUpstream(
  domain: string,
  options?: UseMutationOptions<unknown, unknown, UpstreamConfig>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (config: UpstreamConfig) => {
      const res = await serviceApiV3.put(`/cdn/domains/${domain}/upstream`, config);
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: manticoreKeys.upstream(domain) });
      options?.onSuccess?.(...args);
    },
  });
}

export function useDeleteUpstream(
  domain: string,
  options?: UseMutationOptions<unknown, unknown, void>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const res = await serviceApiV3.delete(`/cdn/domains/${domain}/upstream`);
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: manticoreKeys.upstream(domain) });
      options?.onSuccess?.(...args);
    },
  });
}

// ============================================
// Origin Rewrite  (GET/PUT/DELETE /cdn/domains/:d/origin-rewrite)
// ============================================
export interface OriginRewriteConfig {
  enabled: boolean;
  rules: Array<Record<string, unknown>>;
}

export function useOriginRewrite(domain: string | undefined) {
  return useQuery({
    queryKey: manticoreKeys.originRewrite(domain ?? ''),
    enabled: !!domain,
    retry: false,
    queryFn: async (): Promise<OriginRewriteConfig | null> => {
      try {
        const res = await serviceApiV3.get<Envelope<unknown>>(`/cdn/domains/${domain}/origin-rewrite`);
        // Core-read envelope: the canonical (vEdge) binding's value, which is
        // then `{ enabled, rules }` or a bare array.
        const data = parseCoreRead<unknown>(res.data?.data).core;
        if (data == null) return null;
        if (Array.isArray(data)) return { enabled: true, rules: data as OriginRewriteConfig['rules'] };
        const obj = data as { enabled?: boolean; rules?: OriginRewriteConfig['rules'] };
        return { enabled: obj.enabled ?? true, rules: obj.rules ?? [] };
      } catch (e) {
        const status = (e as { response?: { status?: number } })?.response?.status;
        if (status === 400 || status === 404) return null;
        throw e;
      }
    },
  });
}

export function useUpdateOriginRewrite(
  domain: string,
  options?: UseMutationOptions<unknown, unknown, OriginRewriteConfig>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ enabled, rules }: OriginRewriteConfig) => {
      const res = await serviceApiV3.put(`/cdn/domains/${domain}/origin-rewrite`, { enabled, rules });
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: manticoreKeys.originRewrite(domain) });
      options?.onSuccess?.(...args);
    },
  });
}

export function useDeleteOriginRewrite(
  domain: string,
  options?: UseMutationOptions<unknown, unknown, void>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const res = await serviceApiV3.delete(`/cdn/domains/${domain}/origin-rewrite`);
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: manticoreKeys.originRewrite(domain) });
      options?.onSuccess?.(...args);
    },
  });
}

// ============================================
// Header Rules  (individual CRUD under /cdn/domains/:d/header-rules)
// ============================================
export interface HeaderRule {
  id: number;
  phase: string; // request | response
  action: string; // set | append | remove
  header_name: string;
  header_value: string;
  condition: string; // wirefilter expression
  priority: number;
}

export function useHeaderRules(domain: string | undefined) {
  return useQuery({
    queryKey: manticoreKeys.headerRules(domain ?? ''),
    enabled: !!domain,
    retry: false,
    queryFn: async (): Promise<HeaderRule[]> => {
      const res = await serviceApiV3.get<Envelope<unknown>>(`/cdn/domains/${domain}/header-rules`);
      const rows = unwrapFanoutArray<Record<string, unknown>>(res.data?.data);
      return rows.map((r) => ({
        id: Number(r.id),
        phase: (r.phase as string) || 'response',
        action: (r.action as string) || 'set',
        header_name: (r.header_name as string) || '',
        header_value: (r.header_value as string) || '',
        condition: (r.condition as string) || '',
        priority: Number(r.priority) || 0,
      }));
    },
  });
}

type HeaderRuleInput = Omit<HeaderRule, 'id'> & { id?: number };

export function useSaveHeaderRule(
  domain: string,
  options?: UseMutationOptions<unknown, unknown, HeaderRuleInput>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...body }: HeaderRuleInput) => {
      const res =
        id && id > 0
          ? await serviceApiV3.put(`/cdn/domains/${domain}/header-rules/${id}`, body)
          : await serviceApiV3.post(`/cdn/domains/${domain}/header-rules`, body);
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: manticoreKeys.headerRules(domain) });
      options?.onSuccess?.(...args);
    },
  });
}

export function useDeleteHeaderRule(
  domain: string,
  options?: UseMutationOptions<unknown, unknown, number>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (ruleId: number) => {
      const res = await serviceApiV3.delete(`/cdn/domains/${domain}/header-rules/${ruleId}`);
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: manticoreKeys.headerRules(domain) });
      options?.onSuccess?.(...args);
    },
  });
}

// ============================================
// Error Pages  (GET/PUT/DELETE /cdn/domains/:d/error-pages)
// ============================================
export interface ErrorPagesConfig {
  enabled: boolean;
  pages: Record<string, string>;
}

export function useErrorPages(domain: string | undefined) {
  return useQuery({
    queryKey: manticoreKeys.errorPages(domain ?? ''),
    enabled: !!domain,
    retry: false,
    queryFn: async (): Promise<ErrorPagesConfig | null> => {
      try {
        const res = await serviceApiV3.get<Envelope<unknown>>(`/cdn/domains/${domain}/error-pages`);
        const raw = res.data?.data;
        // `{}` (no vendor key) = error pages never configured → null.
        if (!raw || typeof raw !== 'object' || Object.keys(raw).length === 0) return null;
        let cfg = raw as Record<string, unknown>;
        // Unwrap fanout map `{ raven: { enabled, pages } }`.
        if (!('enabled' in cfg) && !('pages' in cfg)) {
          const first = Object.values(cfg)[0];
          if (first && typeof first === 'object') cfg = first as Record<string, unknown>;
        }
        return { enabled: Boolean(cfg.enabled), pages: (cfg.pages as Record<string, string>) || {} };
      } catch (e) {
        const status = (e as { response?: { status?: number } })?.response?.status;
        if (status === 400 || status === 404) return null;
        throw e;
      }
    },
  });
}

export function useUpdateErrorPages(
  domain: string,
  options?: UseMutationOptions<unknown, unknown, ErrorPagesConfig>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (config: ErrorPagesConfig) => {
      const res = await serviceApiV3.put(`/cdn/domains/${domain}/error-pages`, config);
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: manticoreKeys.errorPages(domain) });
      options?.onSuccess?.(...args);
    },
  });
}

export function useDeleteErrorPages(
  domain: string,
  options?: UseMutationOptions<unknown, unknown, void>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const res = await serviceApiV3.delete(`/cdn/domains/${domain}/error-pages`);
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: manticoreKeys.errorPages(domain) });
      options?.onSuccess?.(...args);
    },
  });
}

// ============================================
// Redirects  (GET/PUT/DELETE /cdn/domains/:d/redirects)
// ============================================
export interface RedirectConditionWire {
  field: string;
  operator: string;
  values: string[];
}
export interface RedirectRuleWire {
  name: string;
  priority: number;
  enabled: boolean;
  destination_url: string;
  status_code: number;
  conditions: RedirectConditionWire[];
}

export function useRedirects(domain: string | undefined) {
  return useQuery({
    queryKey: manticoreKeys.redirects(domain ?? ''),
    enabled: !!domain,
    retry: false,
    queryFn: async (): Promise<{ rules: RedirectRuleWire[] } | null> => {
      try {
        const res = await serviceApiV3.get<Envelope<unknown>>(`/cdn/domains/${domain}/redirects`);
        // Core-read envelope: the canonical (vEdge) binding's value, which is
        // then `{ rules }` or a bare array.
        const data = parseCoreRead<unknown>(res.data?.data).core;
        if (data == null) return null;
        if (Array.isArray(data)) return { rules: data as RedirectRuleWire[] };
        const obj = data as { rules?: RedirectRuleWire[] };
        return { rules: obj.rules ?? [] };
      } catch (e) {
        const status = (e as { response?: { status?: number } })?.response?.status;
        if (status === 400 || status === 404) return null;
        throw e;
      }
    },
  });
}

export function useUpdateRedirects(
  domain: string,
  options?: UseMutationOptions<unknown, unknown, RedirectRuleWire[]>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (rules: RedirectRuleWire[]) => {
      const res = await serviceApiV3.put(`/cdn/domains/${domain}/redirects`, { rules });
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: manticoreKeys.redirects(domain) });
      options?.onSuccess?.(...args);
    },
  });
}

export function useDeleteRedirects(
  domain: string,
  options?: UseMutationOptions<unknown, unknown, void>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const res = await serviceApiV3.delete(`/cdn/domains/${domain}/redirects`);
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: manticoreKeys.redirects(domain) });
      options?.onSuccess?.(...args);
    },
  });
}

// ============================================
// Cache Policies  (GET/PUT/DELETE /cdn/domains/:d/cache-policies)
// ----------------------------------------------------------------------------
// Per-URL cache override rules, evaluated by priority (lower wins) on top of the
// global /cache config. BE wire shape mirrors providers.CDNCachePolicyRule. GET
// fans out `{ data: { "<vendor>": { rules: [...] } } }`; only vEdge/Raven
// currently persists — other vendors return ErrUnsupported (ignored best-effort).
// ============================================
export interface CachePolicyConditionWire {
  field: string;
  operator: string;
  values: string[];
}
export interface CachePolicyResponseHeaderWire {
  action: string; // set | append | remove
  name: string;
  value: string;
}
export interface CachePolicyRuleWire {
  name: string;
  priority: number;
  enabled: boolean;
  conditions: CachePolicyConditionWire[];
  no_cache: boolean;
  enable_x_cache: boolean;
  ignore_no_cache_origin: boolean;
  ignore_no_cache_client: boolean;
  disable_auto_compression: boolean;
  ignore_query_string: boolean;
  edge_ttl_secs: number;
  browser_ttl_secs: number;
  stale_while_revalidate_secs: number;
  stale_if_error_secs: number;
  cache_key?: CacheKeyConfig | null;
  vary?: CacheVaryConfig | null;
  vary_headers: string[];
  response_headers: CachePolicyResponseHeaderWire[];
}

export function useCachePolicies(domain: string | undefined) {
  return useQuery({
    queryKey: manticoreKeys.cachePolicies(domain ?? ''),
    enabled: !!domain,
    retry: false,
    queryFn: async (): Promise<{ rules: CachePolicyRuleWire[] } | null> => {
      try {
        const res = await serviceApiV3.get<Envelope<unknown>>(`/cdn/domains/${domain}/cache-policies`);
        // BE fans out: `{ data: { "<provider>": { rules } | [...] } }`.
        const data = firstVendor<unknown>(res.data?.data, null);
        if (data == null) return null;
        if (Array.isArray(data)) return { rules: data as CachePolicyRuleWire[] };
        const obj = data as { rules?: CachePolicyRuleWire[] };
        return { rules: obj.rules ?? [] };
      } catch (e) {
        const status = (e as { response?: { status?: number } })?.response?.status;
        if (status === 400 || status === 404) return null;
        throw e;
      }
    },
  });
}

export function useUpdateCachePolicies(
  domain: string,
  options?: UseMutationOptions<unknown, unknown, CachePolicyRuleWire[]>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (rules: CachePolicyRuleWire[]) => {
      const res = await serviceApiV3.put(`/cdn/domains/${domain}/cache-policies`, { rules });
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: manticoreKeys.cachePolicies(domain) });
      options?.onSuccess?.(...args);
    },
  });
}

export function useDeleteCachePolicies(
  domain: string,
  options?: UseMutationOptions<unknown, unknown, void>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const res = await serviceApiV3.delete(`/cdn/domains/${domain}/cache-policies`);
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: manticoreKeys.cachePolicies(domain) });
      options?.onSuccess?.(...args);
    },
  });
}
